import { useDataConfigSearch } from '@/hooks/data/useDataConfigSearch'
import { useModule, useModules } from '@/hooks/useModule'
import { useNewAutocomplete } from '@/hooks/useNewAutocomplete'
import {
	useSearchActions,
	useSearchModuleActions,
	useSetSearchBarActions,
} from '@/hooks/useSearch'
import { useTranslator } from '@/hooks/useTranslator'
import { routes } from '@/routes'
import type { CustomLocationHook } from '@/types/useCustomLocation'
import { matchSearchRule } from '@/util/autocomplete'
import type { AutocompleteResult } from '@common/types/AutocompleteResult'
import { useMemo, useRef } from 'react'
import { useLocation } from 'wouter'
import { NewControlledAutocomplete } from '../ui/Autocomplete/NewAutocompleteInput'
import styles from './SearchBar.scss'

function SearchBar() {
	const currentModule = useModule()
	const modules = useModules()
	const [path, setLocation] = useLocation<CustomLocationHook>()
	const formRef = useRef<HTMLFormElement>(null)
	const translator = useTranslator()

	const { search, setSearch, results } = useNewAutocomplete({
		search: '',
		types: ['address', 'area', 'boundary', 'line', 'poi'],
	})
	const { configSearch } = useDataConfigSearch({
		level: 0,
		disable: !search,
	})
	const forwardedResults = useMemo(
		() => (configSearch ? results : []),
		[configSearch, results],
	)

	// send to the search target when selecting a result, and store in context
	const moduleActions = useSearchModuleActions()
	const { setResult } = useSearchActions()
	const onSelect = (result: AutocompleteResult) => {
		setResult(result)

		// find rules that match our current mode and result
		if (!configSearch) return
		const rules = configSearch.targets.filter(rule =>
			matchSearchRule(result, rule, currentModule),
		)

		// find a searchTarget that matches the rules
		let target: string | null = null
		for (const rule of rules) {
			if (target) break
			for (const module of modules) {
				if (rule.target.mode && rule.target.mode !== module.mode)
					continue
				if (rule.target.module !== module.module) continue
				target =
					routes[module.module]?.searchTarget?.(module, result) ??
					null
				if (target) break
			}
		}
		if (!target) {
			console.error('No searchTarget found for search', { result, rules })
			return
		}

		// jump to the target
		if (path !== target) {
			setLocation(target)
		}

		// run acceptSearch on the target
		moduleActions
			.waitForPath(target)
			.then(moduleActions => {
				moduleActions.acceptSearch?.(result)
			})
			.catch(console.error)
	}

	// ensure we don't get half selection
	const onChange = () => {
		// just clear the search on the current module
		moduleActions.current?.clearSearch?.()
		setResult(null)
	}

	// expose the clear function to the context too
	const clearSearch = () => {
		setSearch('')
		setResult(null)
		moduleActions.current?.clearSearch?.()
	}
	useSetSearchBarActions({ clearSearch })

	return (
		<form className={styles.searchBar} ref={formRef}>
			{/* eslint-disable-next-line jsx-a11y/label-has-associated-control*/}
			<label className={styles.labelSearchBar}>
				<NewControlledAutocomplete
					search={search}
					setSearch={setSearch}
					results={forwardedResults}
					onSelect={onSelect}
					onChange={onChange}
					placeholder={translator.translate(
						'board:search-placeholder',
					)}
					isSearchBar
					resetButtonClass={styles.resetButton}
					glassClass={styles.glass}
				/>
			</label>
		</form>
	)
}

export default SearchBar
